// What the Android app shows after "Collect" on the app bonus (apps/web/src/lib/appBonus.ts), and
// what the support assistant reads back to a user asking about it (apps/api/src/lib/support.ts).
// The messages are marked with msg() (docs/I18N.md), so they're in the web and server catalogs: the
// site shows `message` in the reader's language with t(message, values); `text` is the English,
// filled in, for the support assistant (its facts stay English).
import { formatMessage, msg } from './i18n.js';

/** How the app's Play Integrity check ended: passed, failed, or it couldn't run or never answered. */
export type AppCheck = 'passed' | 'failed' | 'unavailable';

export interface AppBonusOutcome {
  ok: boolean;
  /** English, marked for translation: show it with t(message, values). */
  message: string;
  /** The message's values (the coins paid). */
  values: Record<string, number>;
  /** The message in English with its values filled in. */
  text: string;
}

const NOT_PAID: Record<string, string> = {
  // i18n: after "Collect" in the Android app: this account was paid the app bonus before
  claimed: msg('You already got the app bonus.'),
  // i18n: after "Collect" in the Android app: another account on this phone got the bonus before
  device_used: msg('This phone already got the app bonus on another account.'),
  // i18n: after "Collect" in the Android app: the bonus is paid once the email address is confirmed
  verify_email: msg('Confirm your email and the coins arrive right away.'),
  // i18n: after "Collect" in the Android app: the app bonus is switched off for now
  off: msg('The app bonus isn’t available right now.'),
};

/** `bonus`: what the app bonus did after a passed check (apps/api/src/lib/bonuses.ts reasons). */
export function appBonusOutcome(
  check: AppCheck,
  bonus: string | undefined,
  coins: number,
): AppBonusOutcome {
  const outcome = (ok: boolean, message: string, values: Record<string, number> = {}) => ({
    ok,
    message,
    values,
    text: formatMessage('en', message, values),
  });
  if (check === 'passed') {
    if (bonus === 'granted')
      return outcome(
        true,
        // i18n: after "Collect" in the Android app: the app bonus was paid
        msg('+{coins, plural, one {# coin} other {# coins}} added. Thanks for using the app.'),
        { coins },
      );
    return outcome(
      false,
      (bonus && Object.hasOwn(NOT_PAID, bonus) ? NOT_PAID[bonus] : undefined) ??
        // i18n: after "Collect" in the Android app: the bonus can't be paid, for a reason not said
        msg('This account or phone can’t get the app bonus.'),
    );
  }
  if (check === 'failed')
    return outcome(
      false,
      // i18n: after "Collect" in the Android app: the Play Integrity check said no
      msg(
        'We couldn’t confirm this is the GaintPlay app from Google Play on a genuine phone, so the bonus can’t be paid.',
      ),
    );
  // The phone couldn't run the check, or never answered: usually an app or Google Play update.
  return outcome(
    false,
    // i18n: after "Collect" in the Android app: the phone couldn't run the check
    msg(
      'The check couldn’t run on this phone yet. Update GaintPlay and Google Play from the Play Store, then try again.',
    ),
  );
}
